/**
 * GraphQL queries and hooks — the Information Layer dashboard widgets added
 * by issue #1655 (part of epic #1650): Data Domain Usage and Data Quality.
 * Both derive their DataDomain -> Service rollup server-side via
 * `DataObject.domainId` + the real `PRODUCED_BY`/`CONSUMED_BY` edges (there
 * is no `USED_BY` edge — see `backend/src/graphql/resolvers/
 * informationWidgetScope.ts` and ADR-1305) — these hooks are just the query
 * surface.
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';

/**
 * Query for the Data Domain Usage widget (issue #1655). Administrators see
 * every domain; other callers see only domains with at least one service
 * owned by one of their own teams.
 */
export const GET_DATA_DOMAIN_USAGE = gql`
  query GetDataDomainUsage {
    dataDomainUsage {
      domains {
        id
        name
        serviceCount
        steward
        stewardName
        classification
        warning
      }
      unassignedStewards
    }
  }
`;

export interface DataDomainUsageEntry {
  id: string;
  name: string;
  serviceCount: number;
  steward: string;
  stewardName: string | null;
  classification: string;
  warning: string | null;
}

export interface DataDomainUsageData {
  dataDomainUsage: {
    domains: DataDomainUsageEntry[];
    unassignedStewards: number;
  };
}

/** Hook for the Data Domain Usage widget's per-domain service counts. */
export function useDataDomainUsage(): QueryResult<DataDomainUsageData> {
  return useQuery(GET_DATA_DOMAIN_USAGE, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Query for the Data Quality widget (issue #1655). `scope` accepts
 * `"my-teams"` (default) or `"all"` (Administrator only).
 */
export const GET_DATA_QUALITY = gql`
  query GetDataQuality($scope: String) {
    dataQuality(scope: $scope) {
      overallScore
      scoredCount
      totalCount
      issues {
        type
        count
        example
      }
      scope
    }
  }
`;

export interface DataQualityIssue {
  type: string;
  count: number;
  example: string;
}

export interface DataQualityData {
  dataQuality: {
    overallScore: number | null;
    scoredCount: number;
    totalCount: number;
    issues: DataQualityIssue[];
    scope: string;
  };
}

export interface DataQualityVariables {
  scope?: string;
}

/** Hook for the Data Quality widget's real-`qualityScore` average. */
export function useDataQuality(scope?: string): QueryResult<DataQualityData, DataQualityVariables> {
  return useQuery(GET_DATA_QUALITY, {
    variables: { scope },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}
